import {
  ConfigObjectType,
  serializeJSONToString,
  useApiQuery,
  TypeWithEtag,
  useLocationQuery,
  useLocationNonQuery,
  LightLayoutNodeType,
  LightPageLayout,
  useLocalStorage,
} from '@wallaby-ui/core';
import {
  MerieSchema,
  useAddDynamicItem,
  TableSorting,
  TablePaging,
  documentContainer,
  ValuesToCopy,
  useRiskFactorToRiskFactorLabel,
} from '@wallaby-ui/document';
import * as React from 'react';
import {useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {HospitalizationBreadcrumbProps, HospitalizationBreadcrumbs} from './HospitalizationBreadcrumbs';
import {
  DEFAULT_HOSPITALIZATION_LABS_FILTERS,
  HospitalizationDetailLabsV2,
  HospitalizationLabsFilters,
  HospitalizationLabsProps,
} from './HospitalizationDetailLabsV2';
import {HospitalizationLeftPaneProps, HospitalizationLeftPaneV2} from './HospitalizationDetailLeftPaneV2';
import {HospitalizationRightPaneProps, HospitalizationRightPaneV2} from './HospitalizationDetailRightPaneV2';
import {
  DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS,
  DEFAULT_HOSPITALIZATION_TIMELINE_SORTING,
  HospitalizationDetailTimelineV2,
  HospitalizationTimelineFilters,
  HospitalizationTimelineProps,
} from './HospitalizationDetailTimelineV2';
import {useTimelineData} from './HospitalizationDiagnosticsTimeline';
import {
  DEFAULT_DOCUMENTS_FILTERS,
  DEFAULT_DOCUMENTS_PAGING,
  DEFAULT_DOCUMENTS_SORTING,
  DocumentsFilters,
} from './HospitalizationDocumentsV2';
import {HospitalizationDocumentProps, HospitalizationDocumentV2} from './HospitalizationDocumentV2';
import {DEFAULT_PH_REC_SORTING} from './HospitalizationPhRecommendationPanelV2';
import {useChartEvents} from './useChartEvents';

// common
type LeftTabs = {
  left: number;
  reuseDocsPage: boolean;
};
const DEFAULT_LEFT_TABS: LeftTabs = {
  left: 0,
  reuseDocsPage: false,
};
type RightTabs = number;
const DEFAULT_RIGHT_TABS: RightTabs = 0;
type PhRecItem = {
  openedItemId?: number;
};
type AreFiltersOpen = {
  documents: boolean;
  timeline: boolean;
  labCharts: boolean;
};
const DEFAULT_ARE_FILTERS_OPEN: AreFiltersOpen = {
  documents: false,
  timeline: false,
  labCharts: false,
};
// DocumentsFilters in HospitalizationDocumentsV2.tsx
export type HospitalizationState = {
  leftTabs: LeftTabs;
  rightTabs: RightTabs;
  areFiltersOpen: AreFiltersOpen;
  labCharts: HospitalizationLabsFilters;
  timeline: HospitalizationTimelineFilters;
  timelineSorting: TableSorting;
  documents: DocumentsFilters;
  documentsSorting: TableSorting;
  documentsPaging: TablePaging;
  phRecSorting: TableSorting;
  phRecItem?: PhRecItem;
};

// patient card, documents
const LeftPane = HospitalizationLeftPaneV2;
// breadcrumb
const BreadcrumbBox = HospitalizationBreadcrumbs;
// timeline
const TimelineBox = HospitalizationDetailTimelineV2;
// labs
const LabsBox = HospitalizationDetailLabsV2;
// risk factors, ph. recommendation
const RightPane = HospitalizationRightPaneV2;
// document detail
const DocumentDetail = HospitalizationDocumentV2;

// hospitalization detail
interface OwnProps {
  config: ConfigObjectType;
  setValuesToCopy: (newValues: ValuesToCopy) => void;
}
type Props = OwnProps;
export const HospitalizationDetailLayoutV2: React.FC<Props> = (props) => {
  const {config, setValuesToCopy} = props;
  const params = useParams();
  const hospitalizationId = params.hospitalizationId ?? '';
  const documentId = params.documentId ?? '';

  const {hospitalizationDetail: hospitalizationDetailUrl} = config.service.url;
  const hospitalizationDetailParams = useMemo(() => ({id: hospitalizationId}), [hospitalizationId]);
  const {
    data: patientCardData,
    isLoading: patientCardLoading,
    isError: patientCardIsError,
    refetch: patientCardRefetch,
  } = useApiQuery<TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']>>(
    [`${hospitalizationDetailUrl}_patientCard`, serializeJSONToString(hospitalizationDetailParams)],
    null,
    {
      axiosConfig: {
        url: `${hospitalizationDetailUrl}/${hospitalizationId}/patient-card`,
      },
    }
  );
  const encounterId = patientCardData?.encounterId;

  const storageIdPrefix = useMemo(() => `/hospitalizedPatients/v2/${hospitalizationId}`, [hospitalizationId]);
  const [query, changeState] = useLocationQuery(storageIdPrefix, true, (query) => [
    query.leftTabs,
    query.rightTabs,
    query.timeline,
    query.phRecItem,
  ]);
  const state: HospitalizationState = useMemo(() => {
    return {
      leftTabs: query?.leftTabs ?? DEFAULT_LEFT_TABS,
      rightTabs: query?.rightTabs ?? DEFAULT_RIGHT_TABS,
      areFiltersOpen: query?.areFiltersOpen ?? DEFAULT_ARE_FILTERS_OPEN,
      labCharts: query?.labCharts ?? DEFAULT_HOSPITALIZATION_LABS_FILTERS,
      timeline: query?.timeline ?? DEFAULT_HOSPITALIZATION_TIMELINE_FILTERS,
      timelineSorting: query?.timelineSorting === undefined ? DEFAULT_HOSPITALIZATION_TIMELINE_SORTING : query?.timelineSorting,
      documents: query?.documents ?? DEFAULT_DOCUMENTS_FILTERS(encounterId),
      documentsSorting: query?.documentsSorting ?? DEFAULT_DOCUMENTS_SORTING,
      documentsPaging: query?.documentsPaging ?? DEFAULT_DOCUMENTS_PAGING,
      phRecSorting: query?.phRecSorting ?? DEFAULT_PH_REC_SORTING,
      phRecItem: query?.phRecItem ?? undefined,
    };
  }, [query, encounterId]);

  const {
    data: riskFactorData,
    isLoading: riskFactorsLoading,
    isError: riskFactorsError,
    refetch: riskFactorsRefetch,
  } = useApiQuery<TypeWithEtag<MerieSchema.components['schemas']['RiskFactorsDto']>>(
    [`${hospitalizationDetailUrl}_riskFactors`, serializeJSONToString(hospitalizationDetailParams)],
    null,
    {
      axiosConfig: {
        url: `${hospitalizationDetailUrl}/${hospitalizationId}/risk-factors`,
      },
    }
  );
  const riskFactors = useMemo(() => riskFactorData?.riskFactors ?? [], [riskFactorData?.riskFactors]);
  const riskFactorsEtag = riskFactorData?._etag;
  // oldRiskFactorIds
  const {nonQuery, setNonQuery} = useLocationNonQuery(storageIdPrefix);
  const oldRiskFactorIds: Set<number> = useMemo(() => {
    return nonQuery.oldRiskFactorIds ?? new Set();
  }, [nonQuery.oldRiskFactorIds]);
  useEffect(() => {
    if (oldRiskFactorIds.size === 0 && riskFactors.length)
      setNonQuery({
        oldRiskFactorIds: new Set(riskFactors.map((v) => v.id)),
      });
  }, [oldRiskFactorIds, riskFactors, setNonQuery]);

  // phrecommendations
  const phListUrl = config?.service.url.hospitalizationPhRecommendationListGet.replace('{evaluatedEncounterId}', hospitalizationId);
  const {
    data: phRecommendationsData,
    isLoading: phRecommendationsLoading,
    isError: phRecommendationsError,
    refetch: phRecommendationsRefetch,
  } = useApiQuery<TypeWithEtag<MerieSchema.components['schemas']['PharmaRecommendationsDto']>>([phListUrl, encounterId], null, {
    axiosConfig: {
      url: phListUrl,
    },
  });

  // chartEvents
  const {
    data: chartEventsData,
    isLoading: chartEventsLoading,
    isError: chartEventsError,
    refetch: chartEventsRefetch,
  } = useChartEvents({
    startDate: state.labCharts.dateRange?.startDate,
    endDate: state.labCharts.dateRange?.endDate,
    hospitalizationId,
    encounterId,
    config,
  });

  // riskFactorToRiskFactorLabel
  const {riskFactorToRiskFactorLabel, riskFactorToRiskFactorLabelLoading} = useRiskFactorToRiskFactorLabel(config);

  // const queryClient = useQueryClient({context: QueryClientContext});
  const reloadPatientCard = React.useCallback(() => {
    // queryClient.invalidateQueries([config.service.url.hospitalizations])
    patientCardRefetch();
  }, [patientCardRefetch]);
  const reloadRiskFactors = React.useCallback(() => {
    riskFactorsRefetch();
    patientCardRefetch();
  }, [riskFactorsRefetch, patientCardRefetch]);
  const reloadRecommendations = React.useCallback(() => {
    phRecommendationsRefetch();
    chartEventsRefetch();
  }, [phRecommendationsRefetch, chartEventsRefetch]);

  const {addDynamicItem} = useAddDynamicItem();
  useEffect(() => {
    addDynamicItem({
      routeId: 'hospitalizedPatientV2',
      id: `${hospitalizationId}`,
      paramsString: `/${hospitalizationId}${documentId ? `/document/${documentId}` : ''}`,
      label: patientCardData?.fullName ?? '',
      isPatient: true,
      cleanLocalStorage: [`${storageIdPrefix}.LQ`, `${storageIdPrefix}.NQ`],
    });
  }, [storageIdPrefix, addDynamicItem, hospitalizationId, documentId, patientCardData?.fullName]);

  const {sortedTimelineEvents, timelineIsLoading, timelineRefetch} = useTimelineData(config, hospitalizationId, state);
  const reloadTimeline = React.useCallback(() => {
    timelineRefetch();
  }, [timelineRefetch]);

  const isError = riskFactorsError || patientCardIsError;
  const isLoading = riskFactorsLoading || patientCardLoading || patientCardData === undefined;

  const documentInfo = documentContainer.useLoadDocument({
    config,
    documentId,
    encounterId: hospitalizationId,
  });
  const documentData = documentInfo.rawDocumentData as MerieSchema.components['schemas']['DocumentViewDto'] | undefined;

  // valuesToCopy
  if (patientCardData) {
    const acc: ValuesToCopy = {
      encounterId: patientCardData.encounterId,
      encounterSrcId: patientCardData.encounterSrcId,
      patientId: patientCardData.patientId,
      patientSrcId: patientCardData.patientSrcId,
    };
    // if (documentId) acc.documentId = documentId;
    setValuesToCopy(acc);
  } else {
    setValuesToCopy(null);
  }
  // layout
  const leftPaneProps: HospitalizationLeftPaneProps = {
    storageIdPrefix,
    data: patientCardData,
    reloadPatientCard,
    config,
    state,
    changeState,
    hospitalizationId,
    encounterId,
    documentId,
  };
  const breadcrumbProps: HospitalizationBreadcrumbProps = {
    state,
    patient: {
      isLoading: patientCardLoading,
      id: patientCardData?.patientId,
      birthNumber: patientCardData?.identificationNumber,
      name: patientCardData?.fullName,
      srcId: patientCardData?.patientSrcId,
    },
    encounter: {
      isLoading: patientCardLoading,
      id: hospitalizationId,
      encounterType: 'HOSPITALIZATION',
      orgShortName: patientCardData?.admissionDepartment,
      startDate: patientCardData?.encounterStartedAt,
      endDate: patientCardData?.encounterEndedAt,
      isDeleted: patientCardData?.encounterIsDeleted,
      isEscort: patientCardData?.encounterIsEscort,
    },
    document: {
      isLoading: patientCardLoading,
      id: documentId,
      name: documentData?.documentName,
      timestamp: documentData?.documentTimestamp,
    },
  };
  const [timelineDefaultScroll, setTimelineDefaultScroll] = useLocalStorage(`${storageIdPrefix}.timelineScroll`, 0);
  const timelineProps: HospitalizationTimelineProps = {
    state,
    changeState,
    storageIdPrefix,
    config,
    riskFactors,
    timelineDefaultScroll,
    setTimelineDefaultScroll,
    sortedTimelineEvents,
    timelineIsLoading,
    encounterId,
    pharmaRecommendations: phRecommendationsData,
  };
  const labsProps: HospitalizationLabsProps = {
    state,
    changeState,
    storageIdPrefix,
    config,
    encounterId,
    pharmaRecommendations: phRecommendationsData,
    chartEvents: chartEventsData,
  };
  // NOTE: admission at INT-A, but then transfer to INT-B -> use INT-B
  // NOTE: allegedly, we should be using `.lastDepartmentId` here
  const defaultDepartmentId =
    patientCardData?.lastDepartmentId ?? patientCardData?.currentTransferDepartmentId ?? patientCardData?.admissionDepartmentId ?? null;
  const rightPaneProps: HospitalizationRightPaneProps = {
    state,
    changeState,
    storageIdPrefix,
    config,
    hospitalizationId,
    defaultDepartmentId,
    riskFactorToRiskFactorLabel,
    patientCardLoading: patientCardLoading || riskFactorToRiskFactorLabelLoading,
    riskFactors,
    riskFactorsEtag,
    oldRiskFactorIds,
    reloadRiskFactors,
    setTimelineDefaultScroll,
    pharmaRecommendations: phRecommendationsData,
    pharmaRecommendationsLoading: phRecommendationsLoading,
    pharmaRecommendationsRefetch: reloadRecommendations,
    reloadTimeline,
  };
  const documentProps: HospitalizationDocumentProps = {
    ...documentInfo,
    storageIdPrefix: `${storageIdPrefix}/document/${documentId}`,
    documentId,
  };
  const layout: LightLayoutNodeType = {
    initSizes: [23, 77],
    minSizes: (minimized) => [minimized ? 52 : 106, 100],
    noGrow: [false, false],
    attributes: [{'data-page-box': false}],
    nodeProps: [leftPaneProps],
    nodes: [
      LeftPane,
      {
        isVertical: true,
        initSizes: [0, 100],
        minSizes: [64, 100],
        noGrow: [true, false],
        attributes: [{'data-page-box': false}],
        nodeProps: [breadcrumbProps],
        nodes: [
          BreadcrumbBox,
          {
            initSizes: [60, 40],
            minSizes: [100, 56],
            attributes: [{}, {'data-page-box': false}],
            nodeKeys: [documentId ? `document.${documentId}` : `encounter.${hospitalizationId}`, null],
            nodeProps: [documentId ? documentProps : {}, rightPaneProps],
            nodes: [
              documentId
                ? DocumentDetail
                : {
                    isVertical: true,
                    initSizes: [60, 40],
                    minSizes: [56, 56],
                    nodes: [TimelineBox, LabsBox],
                    nodeProps: [timelineProps, labsProps],
                  },
              RightPane,
            ],
          },
        ],
      },
    ],
  };
  return (
    <LightPageLayout
      key={hospitalizationId}
      loading={isLoading}
      isError={isError}
      storageId={'/LightSplitLayout.HospitalizationDetailLayoutV2'}
      layout={layout}
    />
  );
};
